import { BusinessDate } from '@obraplan/core/domain/date/business-date';
import type { PeriodBucket } from '@obraplan/core/domain/reports/hours-report';
import type { PeriodProgress } from '@obraplan/core/domain/reports/indicators';
import { formatBusinessDate, formatShortDate } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * A régua do período.
 *
 * O painel é sobre um intervalo de dias, e esta faixa é o intervalo desenhado: cada
 * trecho é um dos períodos do agrupamento (semana, mês ou dia), a parte cheia é o quanto
 * dele a conferência já cobriu, e o marcador é o hoje da obra. Numa olhada, quem chega
 * sabe três coisas — onde estamos no mês, até onde os números de "realizado" valem, e
 * quanto ainda falta conferir.
 *
 * Nada aqui é decidido só pela cor: a legenda embaixo diz em palavras o que a faixa
 * mostra, e o marcador tem a data escrita.
 */
export interface PeriodRulerProps {
  readonly from: string;
  readonly to: string;
  readonly today: string;
  readonly buckets: readonly PeriodBucket[];
  readonly progress: PeriodProgress;
  readonly inspectedCells: number;
  readonly totalCells: number;
  readonly className?: string;
}

/** Acima disto, os rótulos dos trechos se atropelam; ficam só os que ancoram a leitura. */
const MAX_LABELED_BUCKETS = 14;

export function PeriodRuler({
  from,
  to,
  today,
  buckets,
  progress,
  inspectedCells,
  totalCells,
  className,
}: PeriodRulerProps) {
  const start = BusinessDate.of(from);
  const totalDays = progress.totalDays;
  const todayIndex = buckets.findIndex((bucket) => today >= bucket.from && today <= bucket.to);

  const segments = buckets.map((bucket, index) => {
    const days = BusinessDate.of(bucket.from).daysUntil(BusinessDate.of(bucket.to)) + 1;
    const share =
      bucket.totals.totalCells === 0 ? 0 : bucket.totals.inspectedCells / bucket.totals.totalCells;
    const labeled =
      buckets.length <= MAX_LABELED_BUCKETS ||
      index === 0 ||
      index === buckets.length - 1 ||
      index === todayIndex;
    return { bucket, basis: (days / totalDays) * 100, share, labeled };
  });

  // Se o agrupamento não cobre o período todo (dias sem célula nenhuma), o restante
  // fica em branco na régua em vez de esticar o último trecho.
  const coveredDays =
    buckets.length === 0
      ? 0
      : start.daysUntil(BusinessDate.of(buckets[buckets.length - 1]!.to)) + 1;
  const trailingBasis = Math.max(0, ((totalDays - coveredDays) / totalDays) * 100);

  const markerPct = Math.min(100, Math.max(0, progress.pct));
  const inspectedLabel =
    totalCells === 0
      ? 'Nenhum dia-recurso planejado no período'
      : `Conferidos ${String(inspectedCells)} de ${String(totalCells)} dias-recurso`;

  const description =
    `Período de ${formatBusinessDate(from)} a ${formatBusinessDate(to)}. ` +
    `Hoje é ${formatBusinessDate(today)}, dia ${String(progress.elapsedDays)} de ${String(totalDays)}. ` +
    `${inspectedLabel}.`;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div role="img" aria-label={description} className="flex flex-col gap-1">
        {/* Rótulos dos trechos — S36, set/26, 18/08 — ancorados no início de cada um. */}
        <div className="flex h-4 items-end" aria-hidden="true">
          {segments.map(({ bucket, basis, labeled }) => (
            <div
              key={bucket.key}
              className="min-w-0 overflow-hidden text-[11px] leading-none tabular text-(--color-content-muted)"
              style={{ flexBasis: `${String(basis)}%` }}
            >
              {labeled && <span className="whitespace-nowrap">{bucket.label}</span>}
            </div>
          ))}
        </div>

        <div className="relative">
          <div className="flex h-3.5 gap-px" aria-hidden="true">
            {segments.map(({ bucket, basis, share }) => (
              <div
                key={bucket.key}
                className="h-full min-w-0 overflow-hidden bg-(--color-border-subtle) first:rounded-l-sm last:rounded-r-sm"
                style={{ flexBasis: `${String(basis)}%` }}
              >
                <div
                  className="h-full bg-(--color-brand)"
                  style={{ width: `${String(share * 100)}%` }}
                />
              </div>
            ))}
            {trailingBasis > 0 && (
              <div className="h-full" style={{ flexBasis: `${String(trailingBasis)}%` }} />
            )}
          </div>

          {/* O hoje da obra: um traço na régua e a data escrita, como a marca de um nível. */}
          <div
            className="pointer-events-none absolute -top-1 -bottom-1 w-0.5 -translate-x-1/2 bg-(--color-content)"
            style={{ left: `${String(markerPct)}%` }}
            aria-hidden="true"
          />
        </div>

        <div className="relative h-4 text-[11px] leading-none tabular" aria-hidden="true">
          <span className="absolute left-0 text-(--color-content-muted)">
            {formatShortDate(from)}
          </span>
          <span
            className={cn(
              'absolute whitespace-nowrap font-medium text-(--color-content)',
              markerPct > 85
                ? '-translate-x-full pr-1'
                : markerPct < 15
                  ? 'pl-1'
                  : '-translate-x-1/2',
            )}
            style={{ left: `${String(markerPct)}%` }}
          >
            hoje · {formatShortDate(today)}
          </span>
          <span className="absolute right-0 text-(--color-content-muted)">
            {formatShortDate(to)}
          </span>
        </div>
      </div>

      <p className="text-xs text-(--color-content-muted)">
        Dia {progress.elapsedDays} de {totalDays} · {inspectedLabel} — a parte cheia da régua
      </p>
    </div>
  );
}
